export const GTM_CONTAINER_ID = 'GTM-59JZ8KLG';
const GTM_CONTAINER_SCRIPT_ATTR = 'data-cove-gtm-container';

type FunnelRouteLocation = {
  pathname: string;
  search: string;
  hash: string;
};

type PushPostSignupFunnelPageViewParams = {
  location: FunnelRouteLocation;
  fromRoute?: string;
  isAuthenticated: boolean;
};

type DataLayerEvent = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
    __coveLoadedGtmContainers?: string[];
  }
}

const POST_SIGNUP_FUNNEL_STAGE_BY_PATH: Record<string, string> = {
  '/signup-success': 'signup_success',
  '/walkthrough': 'quick_walkthrough',
  '/create': 'create_entry',
  '/create/fresh': 'create_fresh',
  '/create/sales': 'create_sales',
  '/dashboard': 'dashboard',
  '/studio': 'studio',
  '/autopilot': 'autopilot',
  '/credits': 'credits',
  '/usage': 'usage',
};

export const getPostSignupFunnelStage = (pathname: string) =>
  POST_SIGNUP_FUNNEL_STAGE_BY_PATH[pathname] || '';

export const isPostSignupFunnelRoute = (pathname: string) =>
  Boolean(getPostSignupFunnelStage(pathname));

export const ensureGtmContainerLoaded = (containerId = GTM_CONTAINER_ID) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const normalizedContainerId = containerId.trim();
  if (!normalizedContainerId) return;

  const loadedContainers =
    window.__coveLoadedGtmContainers || (window.__coveLoadedGtmContainers = []);
  if (loadedContainers.includes(normalizedContainerId)) return;

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[${GTM_CONTAINER_SCRIPT_ATTR}="${normalizedContainerId}"]`,
  );
  if (existingScript) {
    loadedContainers.push(normalizedContainerId);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(normalizedContainerId)}`;
  script.setAttribute(GTM_CONTAINER_SCRIPT_ATTR, normalizedContainerId);

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  loadedContainers.push(normalizedContainerId);
};

export const pushPostSignupFunnelPageView = ({
  location,
  fromRoute = '',
  isAuthenticated,
}: PushPostSignupFunnelPageViewParams) => {
  if (!isAuthenticated || typeof window === 'undefined' || typeof document === 'undefined') return;

  const funnelStage = getPostSignupFunnelStage(location.pathname);
  if (!funnelStage) return;

  const route = `${location.pathname}${location.search}${location.hash}`;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'post_signup_funnel_page_view',
    funnel_name: 'post_signup',
    funnel_stage: funnelStage,
    route,
    route_pathname: location.pathname,
    page_path: route,
    page_location: window.location.href,
    page_title: document.title,
    from_route: fromRoute || undefined,
    is_authenticated: true,
  });
};
